import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, CheckCircle2, Mail, Send, Loader2, MailCheck } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import Avatar from '../../components/Avatar';
import { Card } from '../../components/ui';
import { adminApi, AdminUserDetail as UserDetail } from '../../api';
import { apiErrorMessage } from '../../utils/errors';

const rand = (n: number) => `R${n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Money: React.FC<{ label: string; value: string; tone?: string }> = ({ label, value, tone = 'text-gray-900' }) => (
  <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`text-lg font-bold ${tone}`}>{value}</p>
  </div>
);

const AdminUserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const userId = Number(id);
  const [u, setU] = useState<UserDetail | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  // Email composer
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [emailState, setEmailState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [emailNote, setEmailNote] = useState('');

  const load = useCallback(async () => {
    try { setU(await adminApi.getUser(userId)); }
    catch (e: any) { setError(apiErrorMessage(e, 'Could not load user')); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const act = async (key: string, fn: () => Promise<any>) => {
    setBusy(key); setError('');
    try { await fn(); await load(); }
    catch (e: any) { setError(apiErrorMessage(e, 'Action failed')); }
    finally { setBusy(''); }
  };

  const sendEmail = async () => {
    if (!subject.trim() || !body.trim()) return;
    setEmailState('sending'); setEmailNote(''); setError('');
    try {
      const r = await adminApi.emailUser(userId, subject.trim(), body);
      setEmailState('sent');
      setEmailNote(r.delivered ? 'Email sent.' : 'Queued (no email provider configured on this environment).');
      setSubject(''); setBody('');
      setTimeout(() => setEmailState('idle'), 2500);
    } catch (e: any) {
      setEmailState('idle');
      setError(apiErrorMessage(e, 'Could not send email'));
    }
  };

  return (
    <AdminLayout>
      <Link to="/admin/users" className="inline-flex items-center gap-1 text-sm text-green-200/80 hover:text-white mb-3">
        <ArrowLeft className="h-4 w-4" /> Back to golfers
      </Link>

      {error && <div className="bg-red-500/15 text-red-200 border border-red-500/30 p-3 rounded-xl text-sm mb-4">{error}</div>}
      {!u ? (
        <div className="text-green-200/80 py-10 text-center">Loading…</div>
      ) : (
        <div className="space-y-4">
          {/* Identity */}
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <Avatar url={u.avatar_url || undefined} name={u.full_name} size="w-14 h-14" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <h1 className="text-lg font-bold text-gray-900 truncate">{u.full_name}</h1>
                  {u.is_admin && <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0" />}
                  {u.is_verified && <CheckCircle2 className="h-4 w-4 text-blue-500 shrink-0" />}
                </div>
                <p className="text-sm text-gray-500 truncate">{u.email}</p>
                <p className="text-xs text-gray-400">
                  {u.phone || 'No phone'} · HCP {u.handicap ?? 'N/A'} · {u.home_club || 'No club'}
                  {u.created_at ? ` · joined ${new Date(u.created_at).toLocaleDateString()}` : ''}
                </p>
              </div>
            </div>
          </Card>

          {/* Money */}
          <Card className="p-4">
            <h2 className="text-sm font-bold text-gray-800 mb-3">Money</h2>
            <div className="grid grid-cols-2 gap-3">
              <Money label="Wallet balance" value={rand(u.balance_zar)} tone="text-emerald-700" />
              <Money label="Reserved in open bets" value={rand(u.reserved_in_open_bets_zar)} />
              <Money label="Lifetime deposits" value={rand(u.lifetime_deposits_zar)} />
              <Money label="Withdrawals paid" value={rand(u.lifetime_withdrawals_paid_zar)} />
              <Money label="Fees paid" value={rand(u.lifetime_fees_zar)} />
              <Money label={`Pending payouts (${u.pending_withdrawals_count})`} value={rand(u.pending_withdrawals_zar)} tone="text-amber-700" />
            </div>
            <div className="mt-3 text-xs text-gray-500">
              Payout account: <span className="font-mono text-gray-700">{u.bank_account_masked}</span>
            </div>
          </Card>

          {/* Activity */}
          <Card className="p-4">
            <h2 className="text-sm font-bold text-gray-800 mb-3">Activity</h2>
            <div className="flex gap-6 text-sm">
              <div><span className="text-2xl font-bold text-gray-900">{u.games_played}</span><p className="text-xs text-gray-500">games played</p></div>
              <div><span className="text-2xl font-bold text-gray-900">{u.bets_won_count}</span><p className="text-xs text-gray-500">bets won</p></div>
            </div>
            {u.recent_transactions.length > 0 && (
              <div className="mt-3 space-y-1">
                <p className="text-xs font-semibold text-gray-500">Recent transactions</p>
                {u.recent_transactions.map((t) => (
                  <div key={t.id} className="flex justify-between text-xs border-t border-gray-100 pt-1">
                    <span className="truncate mr-2 text-gray-600">{t.description || t.type}</span>
                    <span className="whitespace-nowrap font-semibold text-gray-800">{rand(t.amount_zar)} <span className="text-gray-400">{t.status}</span></span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Actions */}
          <Card className="p-4">
            <h2 className="text-sm font-bold text-gray-800 mb-3">Actions</h2>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => act('verify', () => adminApi.setUserVerified(u.id, !u.is_verified))}
                disabled={!!busy}
                className="py-2.5 rounded-xl text-sm font-semibold border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-60"
              >
                {busy === 'verify' ? '…' : u.is_verified ? 'Mark unverified' : 'Mark verified'}
              </button>
              <button
                onClick={() => act('admin', () => adminApi.setUserAdmin(u.id, !u.is_admin))}
                disabled={!!busy}
                className="py-2.5 rounded-xl text-sm font-semibold border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-60"
              >
                {busy === 'admin' ? '…' : u.is_admin ? 'Revoke admin' : 'Make admin'}
              </button>
              {!u.is_verified && (
                <button
                  onClick={() => act('resend', () => adminApi.resendVerification(u.id))}
                  disabled={!!busy}
                  className="col-span-2 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-60 inline-flex items-center justify-center gap-1"
                >
                  <MailCheck className="h-4 w-4" /> {busy === 'resend' ? 'Sending…' : 'Resend verification email'}
                </button>
              )}
            </div>
          </Card>

          {/* Email */}
          <Card className="p-4">
            <h2 className="text-sm font-bold text-gray-800 mb-3 inline-flex items-center gap-1"><Mail className="h-4 w-4" /> Email this user</h2>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-800 mb-2 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={`Message to ${u.full_name}…`}
              rows={4}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-800 mb-2 focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
            />
            {emailNote && <p className="text-xs text-emerald-700 mb-2">{emailNote}</p>}
            <button
              onClick={sendEmail}
              disabled={emailState === 'sending' || !subject.trim() || !body.trim()}
              className="w-full bg-emerald-600 text-white py-2.5 rounded-xl font-semibold inline-flex items-center justify-center gap-2 hover:bg-emerald-700 transition disabled:opacity-60"
            >
              {emailState === 'sending' ? <Loader2 className="h-4 w-4 animate-spin" /> : emailState === 'sent' ? <MailCheck className="h-4 w-4" /> : <Send className="h-4 w-4" />}
              {emailState === 'sending' ? 'Sending…' : emailState === 'sent' ? 'Sent' : 'Send email'}
            </button>
            <p className="text-xs text-gray-400 mt-2">The user's email address is never shown to other players. This send is recorded in the audit log.</p>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminUserDetail;
